import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { MessageCircle, CreditCard, Loader2, CheckCircle2, Home, Truck, Smartphone, Wallet, Copy, CalendarIcon, ShieldCheck } from "lucide-react";
import { format } from "date-fns";
import { es as esLocale, enUS } from "date-fns/locale";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useApp } from "../AppContext";
import api from "../api";
import { toast } from "sonner";
import LoyaltyBadge from "./LoyaltyBadge";
import { FlagStripe } from "./FlagAccents";

const OrderSection = () => {
    const { t, lang, cart, cartTotal, clearCart, fmtMoney, branding, apiBase } = useApp();
    const [form, setForm] = useState({
        customer_name: "",
        customer_phone: "",
        customer_email: "",
        delivery_address: "",
        notes: "",
        payment_method: "whatsapp",
        delivery_option: "pickup",
        requested_date: null,
    });
    const [submitting, setSubmitting] = useState(false);
    const [lastOrderId, setLastOrderId] = useState(null);
    const [lastWhatsappUrl, setLastWhatsappUrl] = useState(null);
    const [config, setConfig] = useState({ delivery_fee: 5, free_delivery_threshold: 100, payment_methods: {} });
    // Presume first-time until we can confirm otherwise (so the promo never charges by mistake)
    const [isFirstTime, setIsFirstTime] = useState(true);

    useEffect(() => {
        api.get("/config")
            .then((r) => setConfig(r.data))
            .catch(() => {});
    }, []);

    // Detect first-time customer by phone for live promo display.
    // If phone is too short to look up, keep presuming first-time (backend re-validates on submit).
    useEffect(() => {
        const digits = (form.customer_phone || "").replace(/[^\d]/g, "");
        if (digits.length < 7) {
            setIsFirstTime(true);
            return;
        }
        const tm = setTimeout(() => {
            api.get(`/loyalty/${digits}`)
                .then((r) => setIsFirstTime(!!r.data.is_first_time))
                .catch(() => setIsFirstTime(true));
        }, 400);
        return () => clearTimeout(tm);
    }, [form.customer_phone]);

    const onChange = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const deliveryFee = useMemo(() => {
        if (form.delivery_option !== "delivery") return 0;
        if (isFirstTime) return 0; // first-delivery promo
        if (cartTotal >= (config.free_delivery_threshold || 100)) return 0;
        return config.delivery_fee || 5;
    }, [form.delivery_option, cartTotal, config, isFirstTime]);

    const grandTotal = cartTotal + deliveryFee;

    const hasPreorder = useMemo(() => cart.some((c) => c.pre_order), [cart]);
    const minLeadHours = hasPreorder ? 72 : 48;
    const minDate = useMemo(() => {
        const d = new Date();
        d.setHours(d.getHours() + minLeadHours);
        return d;
    }, [minLeadHours]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.customer_name.trim() || !form.customer_phone.trim()) {
            toast.error(t.order.requiredFields);
            return;
        }
        if (form.delivery_option === "delivery" && !form.delivery_address.trim()) {
            toast.error(t.order.addressRequired);
            return;
        }
        if (cart.length === 0) {
            toast.error(t.order.emptyCart);
            return;
        }
        if (!form.requested_date) {
            toast.error(
                lang === "es"
                    ? `Selecciona la fecha preferida (mínimo ${minLeadHours} horas)`
                    : `Pick your preferred date (minimum ${minLeadHours} hours)`
            );
            return;
        }
        setSubmitting(true);
        // CRITICAL: open the WhatsApp tab synchronously inside the user gesture
        // so browsers don't block it. We then navigate it once the API replies.
        // If the browser still blocks it, we show a fallback button.
        const popup = window.open("about:blank", "_blank");
        try {
            const payload = {
                ...form,
                requested_date: format(form.requested_date, "yyyy-MM-dd"),
                items: cart.map((c) => ({
                    item_id: c.item_id,
                    name: lang === "es" ? c.name_es : c.name_en,
                    price: c.price,
                    quantity: c.quantity,
                    pre_order: !!c.pre_order,
                })),
            };
            const res = await api.post(`${apiBase}/orders`, payload);
            const orderId = res.data.order.id;
            const url = res.data.whatsapp_url;
            setLastOrderId(orderId);
            setLastWhatsappUrl(url);
            clearCart();
            toast.success(t.order.success);
            setForm((f) => ({
                ...f,
                customer_name: "",
                customer_phone: "",
                customer_email: "",
                delivery_address: "",
                notes: "",
                requested_date: null,
            }));
            if (popup && !popup.closed) {
                try {
                    popup.location.href = url;
                } catch {
                    // Some browsers (mobile Safari) revoke access — fall back to nav
                    window.location.href = url;
                }
            } else {
                // Popup blocked → let the success card's fallback button take over.
                // Also attempt a same-tab redirect after a beat so mobile users get there too.
                setTimeout(() => {
                    if (document.visibilityState === "visible") window.location.href = url;
                }, 250);
            }
        } catch (err) {
            if (popup && !popup.closed) popup.close();
            toast.error(err?.response?.data?.detail || t.common.error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="order"
            data-testid="order-section"
            className="relative py-24 sm:py-32 bg-earth-50 grain"
        >
            {branding.decorations_enabled && (
                <FlagStripe thickness="h-1.5" className="absolute top-0 left-0 right-0 opacity-70" />
            )}
            <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="text-center mb-14">
                    <span className="pill-tag mb-5">{t.order.tag}</span>
                    <h2 className="font-serif font-black text-earth-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                        {t.order.title}
                    </h2>
                </div>

                <div className="grid md:grid-cols-3 gap-5 mb-12">
                    {[t.order.step1, t.order.step2, t.order.step3].map((step, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="bg-white border border-earth-200 rounded-2xl p-5 flex items-start gap-4"
                        >
                            <div className="font-serif font-black text-3xl text-terracotta-500 leading-none">
                                {i + 1}
                            </div>
                            <p className="text-earth-800 font-semibold text-sm leading-snug">
                                {step}
                            </p>
                        </motion.div>
                    ))}
                </div>

                <form
                    onSubmit={submit}
                    data-testid="order-form"
                    className="bg-white border border-earth-200 rounded-[32px] p-7 sm:p-10 shadow-md"
                >
                    {cart.some((c) => c.pre_order) && (
                        <div
                            data-testid="preorder-notice"
                            className="mb-6 p-4 rounded-2xl flex items-start gap-3"
                            style={{ background: "#002D6210", border: "1px solid #002D6240" }}
                        >
                            <div
                                className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-white text-base"
                                style={{ background: "#002D62" }}
                            >
                                ⏳
                            </div>
                            <div className="text-sm">
                                <div className="font-bold" style={{ color: "#002D62" }}>
                                    {lang === "es"
                                        ? "Tu pedido incluye artículos de pre-orden"
                                        : "Your order includes pre-order items"}
                                </div>
                                <div className="text-earth-700 mt-0.5 text-xs leading-relaxed">
                                    {lang === "es"
                                        ? "Estos requieren 72 horas de anticipación. Te confirmaremos por WhatsApp la fecha exacta de entrega."
                                        : "These require 72 hours notice. We'll confirm the exact delivery date via WhatsApp."}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Confirmation flow notice — visible to all customers */}
                    <div
                        data-testid="confirmation-notice"
                        className="mb-6 p-4 rounded-2xl flex items-start gap-3"
                        style={{ background: "#fdf6ee", border: "1px solid #ecb087" }}
                    >
                        <div className="w-9 h-9 rounded-full bg-terracotta-500 text-white flex items-center justify-center shrink-0">
                            <ShieldCheck className="w-4 h-4" />
                        </div>
                        <div className="text-sm">
                            <div className="font-bold text-earth-900">
                                {lang === "es"
                                    ? "Tu pedido será confirmado por WhatsApp"
                                    : "Your order will be confirmed via WhatsApp"}
                            </div>
                            <div className="text-earth-700 mt-0.5 text-xs leading-relaxed">
                                {lang === "es"
                                    ? `Te contactaremos al número que indiques para confirmar tu pedido. Solo después de confirmar comenzamos a cocinar. Pedidos requieren mínimo ${minLeadHours} horas de anticipación${hasPreorder ? " (incluye pre-orden 72h)" : ""}.`
                                    : `We'll contact you on the phone number you provide to confirm your order. We only start cooking after confirmation. Orders require at least ${minLeadHours} hours notice${hasPreorder ? " (includes 72h pre-order)" : ""}.`}
                            </div>
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                        <FormField
                            label={t.order.name}
                            name="customer_name"
                            value={form.customer_name}
                            onChange={onChange}
                            required
                            testId="order-name-input"
                        />
                        <FormField
                            label={t.order.phone}
                            name="customer_phone"
                            value={form.customer_phone}
                            onChange={onChange}
                            required
                            testId="order-phone-input"
                        />
                        <FormField
                            label={t.order.email}
                            name="customer_email"
                            type="email"
                            value={form.customer_email}
                            onChange={onChange}
                            testId="order-email-input"
                        />
                        <FormField
                            label={t.order.address + (form.delivery_option === "delivery" ? " *" : " (opcional)")}
                            name="delivery_address"
                            value={form.delivery_address}
                            onChange={onChange}
                            testId="order-address-input"
                            required={form.delivery_option === "delivery"}
                        />
                    </div>

                    {/* Delivery option */}
                    <div className="mt-7">
                        <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-3">
                            {t.order.deliveryMethod}
                        </label>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <DeliveryOption
                                active={form.delivery_option === "pickup"}
                                onClick={() =>
                                    setForm((f) => ({ ...f, delivery_option: "pickup" }))
                                }
                                icon={Home}
                                title={t.order.pickup}
                                sub={t.order.pickupSub}
                                testId="delivery-pickup-btn"
                            />
                            <DeliveryOption
                                active={form.delivery_option === "delivery"}
                                onClick={() =>
                                    setForm((f) => ({ ...f, delivery_option: "delivery" }))
                                }
                                icon={Truck}
                                title={t.order.delivery}
                                sub={
                                    isFirstTime
                                        ? (lang === "es" ? "GRATIS · 1er pedido 🎁" : "FREE · 1st order 🎁")
                                        : cartTotal >= (config.free_delivery_threshold || 100)
                                        ? `${lang === "es" ? "Gratis" : "Free"} ✓`
                                        : `${fmtMoney(config.delivery_fee || 5)} · ${t.order.deliveryFree}`
                                }
                                testId="delivery-home-btn"
                            />
                        </div>
                    </div>

                    {/* Preferred date — enforces 48h / 72h lead time */}
                    <div className="mt-7">
                        <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-3">
                            {lang === "es" ? "Fecha preferida" : "Preferred date"}
                            <span className="text-terracotta-500"> *</span>
                            <span className="ml-2 text-earth-500 text-[10px] normal-case font-medium tracking-normal">
                                ({lang === "es" ? `mínimo ${minLeadHours}h` : `minimum ${minLeadHours}h`})
                            </span>
                        </label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    data-testid="order-date-btn"
                                    className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500 flex items-center gap-2 text-left"
                                >
                                    <CalendarIcon className="w-4 h-4 text-earth-500" />
                                    <span className={form.requested_date ? "text-earth-900" : "text-earth-400"}>
                                        {form.requested_date
                                            ? format(form.requested_date, "PPP", { locale: lang === "es" ? esLocale : enUS })
                                            : lang === "es"
                                            ? "Selecciona fecha"
                                            : "Pick a date"}
                                    </span>
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={form.requested_date}
                                    onSelect={(d) => setForm((f) => ({ ...f, requested_date: d }))}
                                    disabled={(d) => d < new Date(minDate.toDateString())}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    </div>

                    {form.customer_phone && (
                        <div className="mt-5">
                            <LoyaltyBadge phone={form.customer_phone} />
                        </div>
                    )}
                    <div className="mt-5">
                        <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">
                            {t.order.notes}
                        </label>
                        <textarea
                            data-testid="order-notes-input"
                            name="notes"
                            value={form.notes}
                            onChange={onChange}
                            rows={3}
                            className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500 transition-colors"
                        />
                    </div>

                    <div className="mt-7">
                        <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-3">
                            {t.order.payment}
                        </label>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <PaymentOption
                                active={form.payment_method === "whatsapp"}
                                onClick={() =>
                                    setForm((f) => ({ ...f, payment_method: "whatsapp" }))
                                }
                                icon={MessageCircle}
                                title={t.order.payCash}
                                sub={lang === "es" ? "Pagas al recibir tu pedido" : "Pay when you receive"}
                                testId="payment-whatsapp-btn"
                                color="leaf"
                            />
                            <PaymentOption
                                active={form.payment_method === "lynk"}
                                onClick={() =>
                                    setForm((f) => ({ ...f, payment_method: "lynk" }))
                                }
                                icon={Smartphone}
                                title="Lynk"
                                sub="🇯🇲 Jamaica · NCB"
                                testId="payment-lynk-btn"
                            />
                            <PaymentOption
                                active={form.payment_method === "tpago"}
                                onClick={() =>
                                    setForm((f) => ({ ...f, payment_method: "tpago" }))
                                }
                                icon={Wallet}
                                title="Tpago"
                                sub="🇩🇴 República Dominicana"
                                testId="payment-tpago-btn"
                            />
                            <PaymentOption
                                active={form.payment_method === "paypal"}
                                onClick={() =>
                                    setForm((f) => ({ ...f, payment_method: "paypal" }))
                                }
                                icon={CreditCard}
                                title="PayPal"
                                sub={lang === "es" ? "Internacional" : "International"}
                                testId="payment-paypal-btn"
                            />
                        </div>
                        {form.payment_method && form.payment_method !== "whatsapp" && config.payment_methods?.[form.payment_method] && (
                            <PaymentDetailsCard
                                method={form.payment_method}
                                info={config.payment_methods[form.payment_method]}
                                lang={lang}
                            />
                        )}
                    </div>

                    <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6 border-t border-earth-200">
                        <div className="space-y-1">
                            <div className="flex items-center gap-3 text-sm">
                                <span className="text-earth-600">{t.order.subtotal}:</span>
                                <span className="font-semibold text-earth-900">{fmtMoney(cartTotal)}</span>
                            </div>
                            {form.delivery_option === "delivery" && (
                                <div className="flex items-center gap-3 text-sm" data-testid="order-delivery-fee">
                                    <span className="text-earth-600">{t.order.deliveryFee}:</span>
                                    {deliveryFee === 0 ? (
                                        <span className="font-semibold text-leaf-600">
                                            {lang === "es" ? "GRATIS" : "FREE"}
                                        </span>
                                    ) : (
                                        <span className="font-semibold text-earth-900">
                                            {fmtMoney(deliveryFee)}
                                        </span>
                                    )}
                                </div>
                            )}
                            <div className="flex items-baseline gap-3 mt-1">
                                <span className="text-xs uppercase tracking-wider text-earth-600 font-bold">
                                    {t.cart.total}
                                </span>
                                <span
                                    data-testid="order-total"
                                    className="font-serif font-black text-3xl text-earth-900"
                                >
                                    {fmtMoney(grandTotal)}
                                </span>
                            </div>
                        </div>
                        <button
                            type="submit"
                            data-testid="order-submit-btn"
                            disabled={submitting || cart.length === 0}
                            className="btn-primary"
                        >
                            {submitting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <MessageCircle className="w-4 h-4" />
                            )}
                            {submitting
                                ? t.order.sending
                                : form.payment_method === "whatsapp"
                                ? t.order.sendWhatsapp
                                : lang === "es" ? "Confirmar y pagar" : "Confirm and pay"}
                        </button>
                    </div>
                    {lastOrderId && lastWhatsappUrl && (
                        <a
                            href={lastWhatsappUrl}
                            target="_blank"
                            rel="noreferrer noopener"
                            data-testid="open-whatsapp-fallback-btn"
                            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-[#25D366] hover:bg-[#1ebe5d] text-white text-base font-bold shadow-lg transition-colors animate-pulse"
                        >
                            <MessageCircle className="w-5 h-5" />
                            {lang === "es"
                                ? "👉 Tocá aquí para enviar tu pedido por WhatsApp a Milly"
                                : "👉 Tap here to send your order via WhatsApp to Milly"}
                        </a>
                    )}
                    {lastOrderId && (
                        <div
                            data-testid="order-tracking-cta"
                            className="mt-4 p-4 rounded-2xl bg-leaf-500/10 border border-leaf-500/30 flex items-center gap-3"
                        >
                            <CheckCircle2 className="w-5 h-5 text-leaf-600 shrink-0" />
                            <div className="text-sm flex-1">
                                <div className="font-bold text-earth-900">
                                    {lang === "es"
                                        ? "¡Pedido recibido!"
                                        : "Order received!"}
                                </div>
                                <div className="text-xs text-earth-700">
                                    {lang === "es" ? "ID:" : "ID:"}{" "}
                                    <code className="text-terracotta-600 font-mono">
                                        {lastOrderId.slice(0, 8).toUpperCase()}
                                    </code>
                                </div>
                            </div>
                            <a
                                href={`/track/${lastOrderId}`}
                                data-testid="order-track-link"
                                className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-leaf-500 text-white text-xs font-semibold hover:bg-leaf-600"
                            >
                                {lang === "es" ? "Rastrear" : "Track"}
                            </a>
                        </div>
                    )}
                    {lastOrderId && (
                        <ConfirmationNotice
                            whatsappNumber={config.whatsapp_number}
                            lang={lang}
                        />
                    )}
                </form>
            </div>
        </section>
    );
};

const FormField = ({ label, name, value, onChange, type = "text", required, testId }) => (
    <div>
        <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">
            {label}
            {required && <span className="text-terracotta-500"> *</span>}
        </label>
        <input
            data-testid={testId}
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            required={required}
            className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500 transition-colors"
        />
    </div>
);

const PaymentOption = ({ active, onClick, icon: Icon, title, sub, testId, color }) => (
    <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className={`flex items-center gap-3 p-4 rounded-2xl border-2 transition-all text-left ${
            active
                ? color === "leaf"
                    ? "border-leaf-500 bg-leaf-500/10"
                    : "border-terracotta-500 bg-terracotta-500/10"
                : "border-earth-200 hover:border-earth-300 bg-earth-50"
        }`}
    >
        <div
            className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                color === "leaf"
                    ? "bg-leaf-500 text-white"
                    : active
                    ? "bg-terracotta-500 text-white"
                    : "bg-earth-200 text-earth-700"
            }`}
        >
            <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-earth-900">{title}</div>
            {sub && <div className="text-xs text-earth-600 truncate">{sub}</div>}
        </div>
        {active && <CheckCircle2 className="w-5 h-5 text-leaf-500 shrink-0" />}
    </button>
);

const PaymentDetailsCard = ({ method, info, lang }) => {
    const copy = (v) => {
        try {
            navigator.clipboard.writeText(v);
            toast.success(lang === "es" ? "Copiado" : "Copied");
        } catch (err) {
            console.warn("Clipboard copy failed:", err);
            toast.error(lang === "es" ? "Error al copiar" : "Copy failed");
        }
    };
    return (
        <div
            data-testid={`payment-details-${method}`}
            className="mt-3 p-4 rounded-2xl border border-dashed border-terracotta-300 bg-terracotta-50"
        >
            <div className="text-[10px] uppercase tracking-wider font-bold text-terracotta-700 mb-2">
                {lang === "es" ? "Datos para transferir" : "Transfer details"}
            </div>
            <div className="space-y-1.5">
                {info.details.map((d) => (
                    <div key={d.label} className="flex items-center gap-2">
                        <span className="text-xs uppercase tracking-wider text-earth-500 font-bold w-16">
                            {d.label}:
                        </span>
                        <code className="text-sm font-mono text-earth-900 select-all">
                            {d.value}
                        </code>
                        <button
                            type="button"
                            onClick={() => copy(d.value)}
                            className="ml-auto p-1.5 rounded hover:bg-white text-earth-600"
                            aria-label="copy"
                        >
                            <Copy className="w-3.5 h-3.5" />
                        </button>
                    </div>
                ))}
            </div>
            <div className="text-xs text-earth-700 mt-3 italic">
                {lang === "es"
                    ? "Después de transferir, envíanos el screenshot por WhatsApp para confirmar."
                    : "After transferring, send us the screenshot via WhatsApp to confirm."}
            </div>
        </div>
    );
};

const DeliveryOption = ({ active, onClick, icon: Icon, title, sub, testId }) => (
    <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className={`flex items-center gap-3 p-4 rounded-2xl border-2 transition-all text-left ${
            active
                ? "border-terracotta-500 bg-terracotta-500/10"
                : "border-earth-200 hover:border-earth-300 bg-earth-50"
        }`}
    >
        <div
            className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                active ? "bg-terracotta-500 text-white" : "bg-earth-200 text-earth-700"
            }`}
        >
            <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-earth-900">{title}</div>
            <div className="text-xs text-earth-600 truncate">{sub}</div>
        </div>
        {active && <CheckCircle2 className="w-5 h-5 text-leaf-500 shrink-0" />}
    </button>
);

const formatWA = (raw) => {
    const d = String(raw || "").replace(/\D/g, "");
    if (d.length === 11 && d.startsWith("1")) return `+1 ${d.slice(1, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
    if (d.length === 10) return `+1 ${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
    return d ? `+${d}` : "";
};

const ConfirmationNotice = ({ whatsappNumber, lang }) => {
    const formatted = formatWA(whatsappNumber);
    const digits = String(whatsappNumber || "").replace(/\D/g, "");
    return (
        <div
            data-testid="order-confirmation-notice"
            className="mt-4 rounded-2xl border-2 border-terracotta-400 bg-terracotta-50 p-5 shadow-sm"
        >
            <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-terracotta-500 text-white flex items-center justify-center shrink-0 font-black">
                    !
                </div>
                <div className="flex-1">
                    <div className="font-serif font-bold text-earth-900 text-lg leading-tight">
                        {lang === "es"
                            ? "Tu pedido aún NO está confirmado"
                            : "Your order is NOT confirmed yet"}
                    </div>
                    <p className="text-sm text-earth-800 mt-2 leading-relaxed">
                        {lang === "es" ? (
                            <>
                                Empezaremos a cocinar <b>solo</b> después de confirmarte por WhatsApp desde{" "}
                                <a
                                    href={digits ? `https://wa.me/${digits}` : "#"}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="font-mono font-bold text-terracotta-700 underline"
                                >
                                    {formatted}
                                </a>
                                . Si no recibes mensaje nuestro en <b>1–2 horas</b>, por favor escríbenos.
                            </>
                        ) : (
                            <>
                                We start cooking <b>only</b> after we confirm via WhatsApp from{" "}
                                <a
                                    href={digits ? `https://wa.me/${digits}` : "#"}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="font-mono font-bold text-terracotta-700 underline"
                                >
                                    {formatted}
                                </a>
                                . If you don't hear from us within <b>1–2 hours</b>, please message us.
                            </>
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default OrderSection;
